import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
} from "@mui/material";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import DashboardNavbar from "components/Navbar";
import DataTable from "components/Tables/DataTable";
import ContentLayout from "layouts/content";
import DateRangePicker from "./components/DateRangePicker";
import donationsTableData from "./data/donationsTableData";

import Loading from "components/Loading";
import Pagination from "components/Pagination";
import {
  setNotification,
  setTotalItems,
  useAdminPanelController,
} from "context";
import debounce from "lodash.debounce";
import { useEffect, useState } from "react";
import { getDonations } from "services/donations";
import { convertDate } from "util/date";
import styles from "./styles.module.scss";

function Donations() {
  const [controller, dispatch] = useAdminPanelController();
  const [donations, setDonations] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [donationDetails, setDonationDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  const [resetKey, setResetKey] = useState(0);

  const perPageContent = 10;

  const handleStatusChange = (e) => {
    setPaymentStatus(e?.target?.value);
    setCurrentPage(1);
  };

  const handleSearch = debounce((e) => {
    setSearch(e?.target?.value);
    setCurrentPage(1);
  }, 400);

  const handleDateRangeChange = (range) => {
    // Only set date range if user explicitly selected dates
    if (range?.from && range?.to) {
      setDateRange(range);
      setCurrentPage(1);
    }
  };

  const handleReset = () => {
    setSearch("");
    setDateRange(null);
    setPaymentStatus(null);
    setCurrentPage(1);
    setResetKey((prev) => prev + 1);
  };

  const fetchData = async () => {
    try {
      const result = await getDonations(
        currentPage,
        search,
        paymentStatus,
        dateRange?.from || null,
        dateRange?.to || null,
        amountMin || null,
        amountMax || null
      );

      setDonations(result?.donations || result || []);
      setTotalItems(dispatch, result?.count || result?.length || 0);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setNotification(dispatch, {
        color: "error",
        icon: "error",
        title: "Error",
        content: error.message || "Failed to fetch donations",
        open: true,
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, search, dateRange]);

  const handleViewDetails = async (donation) => {
    setDonationDetails(donation);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedDonation(null);
    setDonationDetails(null);
  };

  const maskPaymentInfo = (text) => {
    if (!text) return "N/A";
    if (text.length <= 4) return text;
    return "****" + text.slice(-4);
  };

  const formatAddress = (address) => {
    if (!address) return "N/A";
    if (typeof address === "string") return address;
    const parts = [];
    if (address.address) parts.push(address.address);
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.zip) parts.push(address.zip);
    if (address.country) parts.push(address.country);
    return parts.length > 0 ? parts.join(", ") : "N/A";
  };

  const { columns, rows } = donationsTableData(
    donations,
    currentPage,
    perPageContent,
    handleViewDetails
  );

  const donation = donationDetails || selectedDonation;

  return (
    <ContentLayout>
      <DashboardNavbar />
      {loading ? (
        <Loading />
      ) : (
        <MDBox pt={6} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                >
                  <Grid
                    container
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    gap={2}
                  >
                    <Grid item xs={12} md={4}>
                      <MDTypography variant="h6" color="white">
                        Contributions
                      </MDTypography>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{ display: "flex", gap: 2 }}
                      spacing={4}
                    >
                      <MDBox>
                        <DateRangePicker
                          key={resetKey}
                          onChange={handleDateRangeChange}
                          className={styles.custom_select}
                        />
                      </MDBox>
                      <TextField
                        key={resetKey}
                        id="search-donations"
                        className={styles.custom_select}
                        sx={{
                          flex: "1 1 200px",
                          input: { color: "rgb(255, 255, 255)" },
                        }}
                        // value={search}
                        onChange={handleSearch}
                        label="Search"
                        placeholder="Name, email..."
                      />
                      <MDButton
                        variant="gradient"
                        color="success"
                        onClick={handleReset}
                      >
                        Reset
                      </MDButton>
                    </Grid>
                  </Grid>
                </MDBox>
                <MDBox pt={3}>
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                  {donations?.length === 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        paddingBottom: "1.5rem",
                      }}
                    >
                      <MDTypography
                        component="a"
                        variant="caption"
                        color="text"
                        fontWeight="medium"
                      >
                        Oops !! No Result Found
                      </MDTypography>
                    </Box>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
          {controller?.totalItems >= perPageContent && (
            <Pagination
              perPageContent={perPageContent}
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
            />
          )}
        </MDBox>
      )}

      {/* Donation Details Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="donation-details-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          id="donation-details-title"
          sx={{ paddingBottom: 2, paddingTop: 2 }}
        >
          Contribution Details
        </DialogTitle>
        <DialogContent sx={{ padding: 4 }}>
          <MDBox>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <MDTypography variant="caption" fontWeight="bold" color="text">
                  Name
                </MDTypography>
                <MDTypography variant="subtitle2" mb={2}>
                  {donation?.first_name && donation?.last_name
                    ? `${donation?.first_name} ${
                        donation?.last_name || ""
                      }`.trim()
                    : "N/A"}
                </MDTypography>
              </Grid>

              <Grid item xs={12} md={6}>
                <MDTypography variant="caption" fontWeight="bold" color="text">
                  Email
                </MDTypography>
                <MDTypography variant="body2" mb={2}>
                  {donation?.email || "N/A"}
                </MDTypography>
              </Grid>

              <Grid item xs={12} md={6}>
                <MDTypography variant="caption" fontWeight="bold" color="text">
                  Phone
                </MDTypography>
                <MDTypography variant="body2" mb={2}>
                  {donation?.phone || "N/A"}
                </MDTypography>
              </Grid>

              {/* <Grid item xs={12} md={6}>
                <MDTypography variant="caption" fontWeight="bold" color="text">
                  Company
                </MDTypography>
                <MDTypography variant="body2" mb={2}>
                  {donation?.company_name || "N/A"}
                </MDTypography>
              </Grid> */}

              <Grid item xs={12} md={6}>
                <MDTypography variant="caption" fontWeight="bold">
                  Contact Address
                </MDTypography>
                <MDTypography variant="body2" mb={2}>
                  {formatAddress({
                    address: donation?.address,
                    city: donation?.city,
                    state: donation?.state,
                    zip: donation?.zip_code,
                    country: donation?.country,
                  })}
                </MDTypography>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <MDTypography variant="caption" fontWeight="bold">
                  Amount
                </MDTypography>
                <MDTypography variant="body2" mb={2}>
                  ${parseFloat(donation?.amount || 0).toFixed(2)}
                </MDTypography>
              </Grid>

              <Grid item xs={12} md={6}>
                <MDTypography variant="caption" fontWeight="bold">
                  Transaction ID
                </MDTypography>
                <MDTypography variant="body2" mb={2}>
                  {donation?.transaction_id}
                </MDTypography>
              </Grid>

              <Grid item xs={12} md={6}>
                <MDTypography variant="caption" fontWeight="bold">
                  User Type
                </MDTypography>
                <MDTypography variant="body2" mb={2}>
                  {donation?.is_registered_user
                    ? "Registered User"
                    : "Guest User"}
                </MDTypography>
              </Grid>

              <Grid item xs={12} md={6}>
                <MDTypography variant="caption" fontWeight="bold">
                  Date & Time
                </MDTypography>
                <MDTypography variant="body2" mb={2}>
                  {donation?.created_at
                    ? convertDate(donation.created_at)
                    : "N/A"}
                </MDTypography>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ padding: 2, paddingTop: 1 }}>
          <MDButton onClick={handleCloseModal} color="secondary">
            Close
          </MDButton>
        </DialogActions>
      </Dialog>
    </ContentLayout>
  );
}

export default Donations;
